// ABOUTME: Virtualised combobox for selecting a course, shared across student-apply and student-list.
// ABOUTME: Uses @tanstack/react-virtual to keep the dropdown performant when the course list is large.
import { Box, HStack } from '@chakra-ui/react';
import { faClock, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useRef } from 'react';
import type { GroupBase, MenuListProps, OptionProps } from 'chakra-react-select';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Combobox } from '../Combobox';
import { Icon } from '../../atoms/Icon';

// Vertical padding of the menu list container (matches Chakra Menu.list py={2} = 8px per side)
const MENU_LIST_PADDING_Y = 8;

export interface CourseOptionWithMetadata {
  label: string;
  value: string;
  metadata?: {
    modes: string;
    campuses: string;
  };
}

// Renders each dropdown option as a Box so hover/selected/dark styles are
// declarative component props rather than style callbacks.
function CustomOption({
  isSelected,
  isFocused,
  isDisabled,
  innerRef,
  innerProps,
  children,
}: OptionProps<CourseOptionWithMetadata, boolean, GroupBase<CourseOptionWithMetadata>>): React.ReactNode {
  let bg = 'chakra-body-bg';
  if (isSelected) bg = 'hover-bg';
  else if (isFocused) bg = 'chakra-subtle-bg';

  let hoverBg = 'chakra-subtle-bg';
  if (isSelected) hoverBg = 'active-bg';
  else if (isFocused) hoverBg = 'hover-bg';

  return (
    <Box
      ref={innerRef}
      {...innerProps}
      bg={bg}
      // CSS hover so first hover is instant — no JS re-render needed.
      _hover={{ bg: hoverBg }}
      color="chakra-body-text"
      fontSize="md"
      px={3}
      py={3}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.4 : undefined}
      pointerEvents={isDisabled ? 'none' : 'auto'}
    >
      {children}
    </Box>
  );
}

interface VirtualMenuListProps {
  children: React.ReactNode;
  maxHeight: number;
  innerRef?: React.Ref<HTMLDivElement>;
  innerProps?: React.HTMLAttributes<HTMLDivElement>;
}

// Renders only the visible portion of the options list to avoid freezing with large course lists
function VirtualMenuList({ children, maxHeight, innerRef, innerProps = {} }: VirtualMenuListProps): React.ReactNode {
  const items = React.Children.toArray(children);
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 68,
    measureElement: el => el.getBoundingClientRect().height,
    overscan: 5,
  });

  // Shared visual styles matching chakra-react-select's default MenuList appearance
  const containerProps = {
    ref: innerRef,
    ...innerProps,
    bg: 'white',
    _dark: { bg: 'gray.700' },
    boxShadow: 'sm',
    borderRadius: 'md',
    borderWidth: '1px',
    py: 2,
    minW: '100%',
    position: 'relative' as const,
  };

  // Non-array children are status messages (e.g. "No options", loading) — render normally
  if (items.length <= 1) {
    return (
      <Box {...containerProps} maxH={`${maxHeight}px`} overflowY="auto">
        {children}
      </Box>
    );
  }

  return (
    <Box {...containerProps}>
      <Box ref={scrollRef} maxH={`${maxHeight - MENU_LIST_PADDING_Y * 2}px`} overflowY="auto">
        <Box height={`${virtualizer.getTotalSize()}px`} position="relative">
          {virtualizer.getVirtualItems().map(virtualItem => (
            <Box
              key={virtualItem.key}
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              transform={`translateY(${virtualItem.start}px)`}
            >
              {items[virtualItem.index]}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export interface CourseComboboxProps {
  id: string;
  name?: string;
  label?: string;
  options: CourseOptionWithMetadata[];
  value: string;
  onChange: (value: string) => void;
  errorText?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
}

export const CourseCombobox: React.FC<CourseComboboxProps> = ({
  id,
  name,
  label = 'Course',
  options,
  value,
  onChange,
  errorText,
  isDisabled,
  isLoading,
  placeholder = 'Search courses',
}) => {
  const formatCourseOptionLabel = useCallback((option: unknown, { context }: { context: 'menu' | 'value' }) => {
    const courseOption = option as CourseOptionWithMetadata;

    if (context === 'value') {
      return (
        <Box noOfLines={1} fontSize="lg">
          {courseOption.label}
        </Box>
      );
    }

    if (!courseOption.metadata) {
      return (
        <Box fontWeight="medium" lineHeight={5}>
          {courseOption.label}
        </Box>
      );
    }

    return (
      <Box flexGrow={1}>
        <Box fontWeight="medium" fontSize="md" lineHeight={5}>
          {courseOption.label}
        </Box>

        <HStack fontSize="xs" color="muted" mt={1}>
          <Box noOfLines={1} width={'50%'}>
            <Icon icon={faClock} mr={1} />
            {courseOption.metadata.modes}
          </Box>

          <Box noOfLines={1}>
            <Icon icon={faLocationDot} mr={1} />
            {courseOption.metadata.campuses}
          </Box>
        </HStack>
      </Box>
    );
  }, []);

  const courseComboboxStyles = {
    valueContainer: (provided: Record<string, unknown>): Record<string, unknown> => ({
      ...provided,
      padding: '8px 16px',
      alignItems: 'center',
    }),
    singleValue: (provided: Record<string, unknown>): Record<string, unknown> => ({
      ...provided,
      fontSize: 'lg',
      margin: 0,
      marginBottom: 0,
    }),
    input: (provided: Record<string, unknown>): Record<string, unknown> => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
  };

  return (
    <Combobox
      label={label}
      id={id}
      name={name}
      options={options}
      value={value ? [value] : []}
      onChange={newValue => onChange((newValue as { value: string })?.value || '')}
      size="lg"
      errorText={errorText}
      selectProps={{
        isInvalid: !!errorText,
        formatOptionLabel: formatCourseOptionLabel,
        isSearchable: true,
        captureMenuScroll: false,
        chakraStyles: courseComboboxStyles,
        isLoading,
        components: {
          MenuList: VirtualMenuList as unknown as React.ComponentType<
            MenuListProps<unknown, boolean, GroupBase<unknown>>
          >,
          Option: CustomOption as React.ComponentType<OptionProps<unknown, boolean, GroupBase<unknown>>>,
        },
      }}
      isDisabled={isDisabled}
      placeholder={placeholder}
    />
  );
};

export default CourseCombobox;
